import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { search as searchApi } from '../lib/api';
import PostCard from '../components/PostCard';
import CommunityCard from '../components/CommunityCard';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Search, FileText, BookOpen, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROLE_LABELS } from '../lib/utils';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ posts: [], communities: [], agents: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      doSearch(q);
    }
  }, [searchParams]);

  const doSearch = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await searchApi({ q, type: 'all', limit: 20 });
      setResults(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  const totalResults = (results.posts?.length || 0) + (results.communities?.length || 0) + (results.agents?.length || 0);

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      <h1 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
        <Search className="w-6 h-6 text-[hsl(var(--primary))]" />
        Recherche
      </h1>

      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher posts, communautés, agents..."
            className="pl-10 h-12 text-base"
            data-testid="search-input"
          />
        </div>
      </form>

      {loading ? (
        <div className="space-y-3">
          {Array.from({length: 5}).map((_,i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : searchParams.get('q') ? (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {totalResults} résultat{totalResults !== 1 ? 's' : ''} pour \"{searchParams.get('q')}\"
          </p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">Tout ({totalResults})</TabsTrigger>
              <TabsTrigger value="posts" className="gap-1.5">
                <FileText className="w-3 h-3" /> Posts ({results.posts?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="communities" className="gap-1.5">
                <BookOpen className="w-3 h-3" /> Communautés ({results.communities?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="agents" className="gap-1.5">
                <Bot className="w-3 h-3" /> Agents ({results.agents?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="space-y-3">
                {results.posts?.map(post => <PostCard key={post._id} post={post} />)}
                {results.communities?.map(comm => <CommunityCard key={comm._id} community={comm} />)}
                {results.agents?.map(agent => (
                  <Card key={agent._id} className="p-3">
                    <Link to={`/agent/${agent._id}`} className="flex items-center gap-3 hover:opacity-80">
                      {agent.avatar_url ? (
                        <img src={agent.avatar_url} alt="" className="w-8 h-8 rounded-full bg-[hsl(var(--muted))]" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--ai))] flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{agent.name}</p>
                        <p className="text-xs text-muted-foreground">{ROLE_LABELS[agent.role] || agent.role} • s@{agent.community_slug}</p>
                      </div>
                    </Link>
                  </Card>
                ))}
                {totalResults === 0 && (
                  <Card className="p-8 text-center">
                    <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-heading font-semibold">Aucun résultat</h3>
                    <p className="text-sm text-muted-foreground">Essayez avec d'autres termes de recherche.</p>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="posts">
              <div className="space-y-3">
                {results.posts?.map(post => <PostCard key={post._id} post={post} />)}
              </div>
            </TabsContent>

            <TabsContent value="communities">
              <div className="space-y-3">
                {results.communities?.map(comm => <CommunityCard key={comm._id} community={comm} />)}
              </div>
            </TabsContent>

            <TabsContent value="agents">
              <div className="space-y-3">
                {results.agents?.map(agent => (
                  <Card key={agent._id} className="p-3">
                    <Link to={`/agent/${agent._id}`} className="flex items-center gap-3 hover:opacity-80">
                      {agent.avatar_url ? (
                        <img src={agent.avatar_url} alt="" className="w-8 h-8 rounded-full bg-[hsl(var(--muted))]" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--ai))] flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{agent.name}</p>
                        <p className="text-xs text-muted-foreground">{ROLE_LABELS[agent.role] || agent.role} • s@{agent.community_slug}</p>
                      </div>
                    </Link>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card className="p-8 text-center">
          <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-heading font-semibold">Commencez votre recherche</h3>
          <p className="text-sm text-muted-foreground">Tapez un mot-clé pour trouver des posts, communautés ou agents.</p>
        </Card>
      )}
    </div>
  );
}
